import { useEffect, useState } from "react";
import { parseRateLimitInfo } from "@/lib/queryClient";

const DEFAULT_FALLBACK_COOLDOWN_MS = 30_000;

/**
 * Shared rate-limit state for the messaging composers (Task #951).
 *
 * Both the conversation reply composer (`app-messages.tsx`) and the
 * new-message modal (`new-message-modal.tsx`) need to react to the
 * attachment-quota 429 response in the same way:
 *   - parse the `RATE_LIMITED` body to get a message + retryAfterMs hint,
 *   - render an inline amber banner above the composer,
 *   - tick a "Try again in M:SS" countdown,
 *   - block attachment-bearing sends until the cooldown elapses,
 *   - keep the typed draft + ready attachments intact for resend.
 *
 * Centralizing the state machine here keeps both surfaces in sync and
 * makes the behavior unit-testable in isolation.
 */
export function useAttachmentRateLimit() {
  const [until, setUntil] = useState<number | null>(null);
  const [message, setMessage] = useState<string>("");
  const [remaining, setRemaining] = useState(0);

  // Tick the countdown once per second while a cooldown is active. When
  // remaining hits zero we clear the message too so the banner disappears
  // and the send button re-enables on its own.
  useEffect(() => {
    if (!until) return;
    const tick = () => {
      const r = Math.max(0, Math.ceil((until - Date.now()) / 1000));
      setRemaining(r);
      if (r <= 0) {
        setUntil(null);
        setMessage("");
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [until]);

  /**
   * Try to arm the rate-limit cooldown from a thrown apiRequest error.
   * Returns the parsed `{ message, retryAfterMs }` if the error was a
   * 429 RATE_LIMITED, or `null` if it was some other error (caller should
   * fall through to its normal error handling).
   *
   * If the server omits `retryAfterMs` we arm a sensible 30s default so
   * the banner is guaranteed to clear on its own and the host isn't
   * permanently blocked from retrying with attachments in-session.
   */
  function armFromError(err: unknown): { message: string; retryAfterMs: number } | null {
    const info = parseRateLimitInfo(err);
    if (!info) return null;
    const cooldownMs = info.retryAfterMs > 0 ? info.retryAfterMs : DEFAULT_FALLBACK_COOLDOWN_MS;
    setUntil(Date.now() + cooldownMs);
    setRemaining(Math.ceil(cooldownMs / 1000));
    setMessage(info.message);
    return info;
  }

  function reset() {
    setUntil(null);
    setMessage("");
    setRemaining(0);
  }

  // Banner visibility — true whenever a cooldown is in flight (we always
  // arm one when armFromError fires, so this collapses to "is there an
  // active rate-limit message").
  const isActive = message.length > 0 && (until === null || remaining > 0);

  return {
    /** Server-supplied (or fallback) human-readable message. */
    message,
    /** Seconds remaining on the cooldown; 0 once it elapses. */
    remaining,
    /** True while the banner should be visible. */
    isActive,
    /** Arm cooldown from a thrown apiRequest error. */
    armFromError,
    /** Clear all rate-limit state (e.g. on modal reopen). */
    reset,
  };
}

/**
 * Format a remaining-seconds value for display in the inline banner and
 * the Send-button label. Mirrors the login page's rate-limit treatment.
 */
export function formatRateLimitCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0) return `${m}:${String(s).padStart(2, "0")}`;
  return `${s}s`;
}
